<!--
  =====================================================================
  MEGA MENU - FULL SCREEN NAVIGATION OVERLAY
  =====================================================================
  
  Full-screen mega menu component displayed on hamburger click.
  
  FEATURES:
  - Dynamic menu columns from store state
  - Top action buttons (banking CTA + foundation logo)
  - Responsive layout (desktop/tablet/mobile)
  - Reordered menu items on desktop (Filiali & Fondazione moved to end)
  - Body scroll lock when menu is open
  - Click backdrop to close
  - Keyboard accessible
  
  LAYOUT:
  - Desktop: Two-column layout with vertical divider
  - Mobile: Single column, centered, stacked buttons
  - Top actions: Banking buttons (left/right) + Foundation logo (center)
  
  =====================================================================
-->

<template>
  <div class="mega-menu" v-if="props.show" @click="handleBackdropClick">
    
    <button class="close-button" @click="props.closeMenu" aria-label="Chiudi menu"></button>

    <div class="columns" @click.stop>
      <nav
        v-for="col in megaMenuItemsReordered"
        :key="col.id"
        class="column"
        :class="`column--${col.id}`"
      >
        <ul>
          <li v-for="item in col.items" :key="item.link" class="menu-item">
            <a 
              :href="item.link"
              :target="item.target || '_self'"
              :rel="item.target === '_blank' ? 'noopener noreferrer' : ''"
            >
              {{ item.label }}
            </a>

            <ul v-if="item.children" class="submenu">
              <li v-for="child in item.children" :key="child.link">
                <a 
                  :href="child.link"
                  :target="child.target || '_self'"
                  :rel="child.target === '_blank' ? 'noopener noreferrer' : ''"
                >
                  {{ child.label }}
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </div>

    <div class="mega-menu__bottom-actions" @click.stop>
      <a
        v-for="btn in topActions"
        :key="btn.id"
        :href="btn.link || '#'"
        :class="['bottom-action', getButtonClass(btn)]"
        :target="btn.target || '_self'"
        :rel="btn.rel || ''"
        :aria-label="btn.ariaLabel || undefined"
      >
        <div class="vertical-bar"></div>
        
        <img
          v-if="btn.image"
          :src="btn.image"
          :alt="(btn.ariaLabel || btn.label) ? '' : (state.header.logoSecondary?.alt || 'Logo')"
          loading="lazy"
          :class="getImageClass(btn)"
        />
        
        <div v-if="btn.label" class="bottom-action__text">
          <span class="bottom-action__text-primary">{{ btn.label }}</span>
          <span v-if="btn.sublabel" class="bottom-action__text-secondary">{{ btn.sublabel }}</span>
        </div>
        
        <span v-else-if="!btn.image" class="bottom-action__fallback">{{ btn.id }}</span>
      </a>
    </div>

    <div class="mega-menu__footer-decoration">
      <img src="/images/aesthetic/ctl_footer_up_fond.png" alt="" />
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { state } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// PROPS & LIFECYCLE
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Boolean} show - Controls menu visibility
 * @prop {Function} closeMenu - Callback to close the menu
 */
const props = defineProps({
  show: { type: Boolean, default: false },
  closeMenu: { type: Function, required: true }
})

/**
 * Watch menu visibility and toggle body scroll lock
 * Prevents page scrolling when mega menu is open
 */
watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  },
  { immediate: true }
)

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - TOP ACTIONS
// ───────────────────────────────────────────────────────────────────

/**
 * Gets top action buttons from global store state
 * Contains up to 3 buttons: left banking, center foundation, right banking
 */
const topActions = computed(() => state.megaMenuTopActions || [])

// ───────────────────────────────────────────────────────────────────
// BUTTON STYLING HELPERS
// ───────────────────────────────────────────────────────────────────

/**
 * Determines CSS classes for button based on type
 * @param {Object} btn - Button object
 * @returns {String|Array} CSS class name(s)
 */
function getButtonClass(btn) {
  if (btn.type === 'foundation' && btn.id === 'cassa') {
    return 'bottom-action--fondazione'
  }
  if (btn.type === 'action') {
    return ['bottom-action--banking', 'bottom-action--banking-left']
  }
  if (btn.type === 'social') {
    return ['bottom-action--banking', 'bottom-action--banking-right']
  }
  return ''
}

/**
 * Determines CSS classes for image element based on button type
 * @param {Object} btn - Button object
 * @returns {String} CSS class name
 */
function getImageClass(btn) {
  if (btn.type === 'foundation' && btn.id === 'cassa') {
    return 'bottom-action__img'
  }
  if (btn.type === 'action' || btn.type === 'social') {
    return 'bottom-action__logo'
  }
  return 'bottom-action__logo'
}

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - MENU ITEMS
// ───────────────────────────────────────────────────────────────────

/**
 * Reorders menu items based on breakpoint
 * Desktop: Moves "Filiali" and "Visita il sito della Fondazione" to end
 * Mobile: Keeps original order
 */
const megaMenuItemsReordered = computed(() => {
  const items = state.megaMenuItems
  if (!items || items.length === 0) return []
  const isMobile = window.innerWidth <= 767

  const reorderedItems = items.map(col => {
    if (col.id === 'primary') {
      const newItems = [...col.items]
      if (!isMobile) {
        let filiali = null
        let fondazione = null
        const filialiIndex = newItems.findIndex(item => item.label === 'Filiali')
        if (filialiIndex > -1) filiali = newItems.splice(filialiIndex, 1)[0]
        const fondazioneIndex = newItems.findIndex(item => item.label === 'Visita il sito della Fondazione')
        if (fondazioneIndex > -1) fondazione = newItems.splice(fondazioneIndex, 1)[0]
        if (filiali) newItems.push(filiali)
        if (fondazione) newItems.push(fondazione)
      }
      return { ...col, items: newItems }
    }
    return col
  })

  return reorderedItems
})

// ───────────────────────────────────────────────────────────────────
// EVENT HANDLERS
// ───────────────────────────────────────────────────────────────────

/**
 * Closes menu when backdrop (outside of menu content) is clicked
 * @param {Event} event - Click event
 */
function handleBackdropClick(event) {
  if (event.target === event.currentTarget) {
    props.closeMenu()
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

/* ------------------------------------------------------------------
   Mega Menu - Base layout
------------------------------------------------------------------ */
.mega-menu {
  position: fixed;
  /* ricaduta iOS < 14.5: longhand prima, `inset` sotto vince sui moderni */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  inset: 0;
  padding: $space-0-5;
  padding-bottom: 0;
  background: $clr-bg-page;
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
  box-sizing: border-box;
}

/* ------------------------------------------------------------------
   Close button (X icon)
------------------------------------------------------------------ */
.close-button {
  position: absolute;
  top: $space-1-5;
  right: $space-1-5;
  width: 60px;
  height: 60px;
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  z-index: 2;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 40px;
    height: 1px;
    background: $text-color;
    transition: background 0.2s ease;
  }

  &::before { transform: translate(-50%, -50%) rotate(45deg); }
  &::after { transform: translate(-50%, -50%) rotate(-45deg); }

  @media (hover: hover) {
    &:hover::before,
    &:hover::after { background: $clr-tertiary; }
  }
}

/* ------------------------------------------------------------------
   Menu columns layout
------------------------------------------------------------------ */
.columns {
  display: flex;
  flex-direction: column;
  @include flex-gap($space-3, column);
  width: 100%;
  max-width: 1400px;
  position: relative;
  box-sizing: border-box;
  margin-bottom: $space-3;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 3px;
    background: $clr-brand-dark;
    transform: translateX(-50%);
    box-shadow: 1px 0 3px rgba($clr-brand-dark, 0.2);
  }
}

@media (min-width: 768px) {
  .columns {
    flex-direction: row;
    justify-content: center;
    @include flex-gap(0);
  }

  .column.column--primary {
    width: 50%;
    padding-left: $space-2;
    padding-right: $space-2;
    text-align: left;
  }

  .column.column--footer {
    width: 50%;
    padding-left: $space-2;
    padding-right: $space-2;
    text-align: right;
  }

  .column--foundation { display: none; }
}

/* ------------------------------------------------------------------
   Menu column styles
------------------------------------------------------------------ */
.column {
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
}

.menu-item {
  margin-bottom: clamp($space-1, 2.5vw, $space-2-5);

  > a {
    text-decoration: none;
    font-size: clamp(1.2rem, 4.5vw, 3.2rem);
    font-weight: 400;
    font-family: 'Karla', sans-serif;
    color: $text-color;
    transition: color 0.3s ease, transform 0.3s ease;
    display: inline-block;
    line-height: 1.2;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: scale(1.1);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Submenu styles
------------------------------------------------------------------ */
.submenu {
  margin-top: clamp($space-0-5, 1.5vw, $space-1-5);
  padding-left: clamp($space-1, 2vw, $space-2);

  li { margin: clamp($space-0-5, 1.2vw, $space-1) 0; }

  a {
    font-size: clamp(1rem, 3.5vw, 2.2rem);
    font-weight: 300;
    font-family: 'Karla', sans-serif;
    color: $clr-text-light;
    text-decoration: none;
    transition: color 0.3s ease, transform 0.3s ease;
    display: inline-block;
    line-height: 1.3;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: scale(1.08);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Bottom actions - CTA buttons
------------------------------------------------------------------ */
.mega-menu__bottom-actions {
  position: relative;
  width: min(1200px, 100% - #{$space-1});
  display: flex;
  @include flex-gap($space-1);
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-sizing: border-box;
  margin-bottom: $space-2;
}

.bottom-action {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  height: 64px;
  background: $clr-white;
  border: 1px solid $clr-border;
  box-shadow: 0 4px 20px rgba($clr-brand-dark, 0.12);
  text-decoration: none;
  transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease;
  box-sizing: border-box;
  padding: 0 $space-0-5;
  overflow: hidden;

  &:focus-visible {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
  }

  /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
     scartata per intero. Si ripiega su :focus, che mostra l'anello
     anche col mouse: e' il degrado accettato. */
  .no-focusvisible &:focus {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:hover,
    &:focus-visible {
      transform: scale(1.02);
      box-shadow: 0 8px 32px rgba($clr-brand-dark, 0.2);
      border-color: rgba($clr-brand-dark, .35);
    }

    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      transform: scale(1.02);
      box-shadow: 0 8px 32px rgba($clr-brand-dark, 0.2);
      border-color: rgba($clr-brand-dark, .35);
    }
  }
}

/* ------------------------------------------------------------------
   Vertical bar animation on buttons
------------------------------------------------------------------ */
.vertical-bar {
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: 100%;
  pointer-events: none;

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 3px;
    background: $clr-border;
    height: 0;
    transition: height 0.5s ease, background 0.5s ease;
  }

  &::before { bottom: 50%; }
  &::after { top: 50%; }
}

@media (hover: hover) {
  .bottom-action:hover .vertical-bar::before,
  .bottom-action:hover .vertical-bar::after {
    height: 50%;
    background: $clr-brand-dark;
  }

  /* Action button - Internet Banking (green accent) */
  .bottom-action--banking-left:hover .vertical-bar::before,
  .bottom-action--banking-left:hover .vertical-bar::after {
    background: $clr-tertiary;
  }

  /* Social button - Corporate Banking (dark accent) */
  .bottom-action--banking-right:hover .vertical-bar::before,
  .bottom-action--banking-right:hover .vertical-bar::after {
    background: $clr-brand-dark;
  }

  /* Foundation button - No bar animation */
  .bottom-action--fondazione:hover .vertical-bar::before,
  .bottom-action--fondazione:hover .vertical-bar::after {
    height: 0;
    background: transparent;
  }

  /* Action button - No bar animation */
  .bottom-action--action:hover .vertical-bar::before,
  .bottom-action--action:hover .vertical-bar::after {
    height: 0;
    background: transparent;
  }

  /* Banking left - logo filter change + text green */
  .bottom-action--banking-left:hover .bottom-action__logo {
    filter: brightness(0) saturate(100%) invert(49%) sepia(11%) saturate(1915%) hue-rotate(52deg) brightness(92%) contrast(82%);
  }

  .bottom-action--banking-left:hover .bottom-action__text-primary,
  .bottom-action--banking-left:hover .bottom-action__text-secondary {
    color: $clr-tertiary !important;
  }

  /* Banking right - text dark */
  .bottom-action--banking-right:hover .bottom-action__text-primary,
  .bottom-action--banking-right:hover .bottom-action__text-secondary {
    color: $clr-brand-dark !important;
  }

  /* Text secondary reveal on hover — row 1 moves up, row 2 fades in */
  .bottom-action--banking:hover .bottom-action__text-secondary {
    max-height: 2em;
    opacity: 1;
  }
}

.bottom-action__fallback {
  font-size: 1rem;
  font-weight: 600;
  color: $clr-brand-gray;
  font-family: 'Karla', sans-serif;
}

/* ------------------------------------------------------------------
   Action/Social button styles
------------------------------------------------------------------ */
.bottom-action--action {
  justify-content: center;
  padding: 0 $space-1;
  min-width: 200px;
}

.bottom-action--banking-left,
.bottom-action--banking-right {
  justify-content: flex-start;
  padding: 0;
  width: 280px;
  min-width: 280px;
}

.bottom-action__img-action {
  height: 60%;
  width: auto;
  max-width: 60px;
  object-fit: contain;
  padding: 0;
  margin: 0;
}

/* ------------------------------------------------------------------
   Banking button styles (legacy)
------------------------------------------------------------------ */
.bottom-action--banking {
  padding: 0;
  min-width: 280px;
}

.bottom-action__logo {
  height: 100%;
  width: auto;
  max-width: 64px;
  object-fit: contain;
  padding: 0.4rem;
  padding-left: 0.9rem;
  margin: 0;
  filter: brightness(0) saturate(100%) invert(18%) sepia(21%) saturate(1547%) hue-rotate(115deg) brightness(96%) contrast(92%);
  transition: filter 0.3s ease;
}

.bottom-action__img {
  height: 80%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  padding: 0;
  margin: 0;
}

.bottom-action__text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  padding-left: 0.6rem;
}

.bottom-action__text-primary {
  font-size: 1.2rem;
  font-weight: 400;
  color: $clr-brand-gray;
  line-height: 1.2;
  font-family: 'Karla', sans-serif;
}

.bottom-action__text-secondary {
  font-size: 0.8rem;
  font-weight: 500;
  color: $clr-brand-gray;
  line-height: 1.2;
  margin-top: 2px;
  font-family: 'Karla', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.35s ease, opacity 0.3s ease 0.1s;
}

/* ------------------------------------------------------------------
   Foundation button styles
------------------------------------------------------------------ */
.bottom-action--fondazione {
  padding: 0 $space-0-5;
  width: 280px;
  height: 64px;
  justify-content: center;
  align-items: center;

  img {
    display: block;
    height: 80%;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    padding: 0;
    margin: 0;
  }
}

/* ------------------------------------------------------------------
   Footer decoration
------------------------------------------------------------------ */
.mega-menu__footer-decoration {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  line-height: 0;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
}

/* ------------------------------------------------------------------
   Display medio-grande (781px – 1300px)
   Il padding-top "blocca" le voci al di sotto della close button:
   con justify-content:center il contenuto è centrato nello spazio
   residuo sotto il padding, quindi non può mai salire oltre quel punto.
   overflow-y:auto permette lo scroll se l'altezza è insufficiente.
------------------------------------------------------------------ */
@media (min-width: 781px) and (max-width: 1300px) and (not (orientation: landscape)) {
  .mega-menu {
    padding-top: calc(#{$space-1-5} + 60px + #{$space-2}); /* 24px + 60px + 32px = 116px */
    overflow-y: auto;
  }
}

/* ------------------------------------------------------------------
   Tablet responsive (1025px - 1024px)
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .mega-menu__bottom-actions {
    @include flex-gap($space-0-75);
    width: min(800px, 100% - #{$space-2});
  }

  .bottom-action,
  .bottom-action--banking,
  .bottom-action--fondazione {
    width: 220px !important;
    height: 48px !important;
    min-width: unset !important;
    max-width: none !important;
  }

  .bottom-action__logo {
    max-width: 60px;
    padding-left: 1rem;
  }

  .bottom-action__text {
    padding-right: 1rem;
  }

  .bottom-action__text-primary {
    font-size: 0.9rem;
  }

  .bottom-action__text-secondary {
    font-size: 0.8rem;
  }

  .bottom-action--fondazione img {
    height: 75%;
  }
}

/* ------------------------------------------------------------------
   Mobile (≤ 780px)
------------------------------------------------------------------ */
@media (max-width: 780px) {
  .mega-menu {
    justify-content: center;
    overflow: hidden;
  }

  .close-button {
    top: $space-0-75;
    right: $space-0-75;
    width: 40px;
    height: 40px;

    &::before,
    &::after { width: 30px; }
  }

  .columns {
    padding-left: $space-1;
    padding-right: $space-1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-bottom: $space-2;

    &::before { display: none; }
  }

  .column {
    width: 100%;
    text-align: center;

    ul { text-align: center; }
  }

  .menu-item {
    margin-bottom: $space-0-75;
    text-align: center;

    > a {
      font-size: 1.1rem;
      display: inline-block;
      text-align: center;
    }
  }

  .submenu {
    margin-top: $space-0-5;
    padding-left: 0;
    text-align: center;

    li { text-align: center; }

    a {
      font-size: 0.95rem;
      display: inline-block;
      text-align: center;
    }
  }

  .mega-menu__bottom-actions {
    flex-direction: column;
    align-items: center;
    @include flex-gap($space-0-5, column);
    width: calc(100% - #{$space-1});
    margin-bottom: $space-1-5;
  }

  .bottom-action {
    width: 70%;
    max-width: 220px;
    height: 48px;
    box-shadow: 0 3px 15px rgba($clr-brand-dark, 0.1);
  }

  .bottom-action--banking {
    width: 70%;
    max-width: 220px;
    min-width: unset;
  }

  .bottom-action__logo {
    max-width: 60px;
    padding-left: 1.2rem;
  }

  .bottom-action__text {
    padding-right: 1.2rem;
  }

  .bottom-action__text {
    padding-left: 0.6rem;
  }

  .bottom-action__text-primary {
    font-size: 0.85rem;
    font-weight: 600;
  }

  .bottom-action__text-secondary {
    font-size: 0.75rem;
    opacity: 1;
    max-height: 2em;
  }

  .bottom-action--fondazione img {
    height: 70%;
  }
}

/* ------------------------------------------------------------------
   Small Tablet (481px - 768px)
------------------------------------------------------------------ */
@media (min-width: 481px) and (max-width: 768px) {
  .menu-item > a { font-size: 1.95rem; }
  .submenu a { font-size: 1.55rem; }
}

/* ------------------------------------------------------------------
   Mobile (≤ 480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .menu-item > a { font-size: 1.5rem; }
  .submenu a { font-size: 1.25rem; }

  .bottom-action,
  .bottom-action--banking,
  .bottom-action--fondazione {
    width: 75%;
    max-width: 200px;
    height: 44px;
  }

  .bottom-action__logo {
    max-width: 55px;
    padding-left: 1rem;
  }

  .bottom-action__text-primary {
    font-size: 0.8rem;
  }

  .bottom-action__text-secondary {
    font-size: 0.7rem;
  }

  .close-button {
    width: 36px;
    height: 36px;
  }

  .columns { margin-bottom: $space-1-5; }
}

/* ------------------------------------------------------------------
   Very Small Mobile (≤ 360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .menu-item > a { font-size: 1.3rem; }
  .submenu a { font-size: 1.1rem; }

  .bottom-action,
  .bottom-action--banking,
  .bottom-action--fondazione {
    width: 80%;
    max-width: 180px;
    height: 40px;
  }

  .bottom-action__logo {
    max-width: 50px;
    padding-left: 0.8rem;
  }

  .bottom-action__text-primary {
    font-size: 0.75rem;
  }

  .bottom-action__text-secondary {
    font-size: 0.65rem;
  }

  .bottom-action--fondazione img {
    height: 65%;
  }
}

/* ------------------------------------------------------------------
   Mobile Landscape (≤ 780px, orientamento orizzontale)
   Il menu è 100vh fisso. Il contenuto si distribuisce verticalmente
   sull'intera altezza disponibile. Margine generoso in basso per
   l'immagine decorativa del castello.
------------------------------------------------------------------ */
@media (max-width: 950px) and (orientation: landscape) {
  .mega-menu {
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    overflow: hidden;
    padding-top: 44px;
    padding-left: $space-0-5;
    padding-right: $space-0-5;
    padding-bottom: 72px; /* spazio generoso per immagine castello */
    @include flex-gap(0);
  }

  .close-button {
    top: $space-0-5;
    right: $space-0-5;
    width: 36px;
    height: 36px;

    &::before,
    &::after { width: 26px; }
  }

  /* Colonne menu: occupano tutta l'altezza disponibile */
  .columns {
    flex-direction: row;
    align-items: stretch;
    justify-content: space-between;
    @include flex-gap(0);
    width: 100%;
    margin-bottom: 0;
    align-self: stretch;

    /* Nessuna barra separatrice */
    &::before { display: none; }
  }

  /* Le colonne si estendono in altezza e distribuiscono le voci */
  .column {
    width: 50%;
    display: flex;
    flex-direction: column;

    ul {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      height: 100%;
      text-align: left;
    }

    &.column--primary {
      padding-left: $space-2;
      padding-right: 18%;
      text-align: left;
    }

    &.column--footer {
      padding-left: 18%;
      padding-right: $space-2;
      text-align: right;

      ul { text-align: right; }
    }
  }

  .menu-item {
    margin-bottom: 0; /* spacing gestito da space-evenly */
    text-align: inherit;

    > a {
      font-size: 1.6rem;
      text-align: inherit;
    }
  }

  .submenu {
    margin-top: $space-0-25;
    padding-left: $space-0-5;
    text-align: inherit;

    li {
      margin: $space-0-25 0;
      text-align: inherit;
    }

    a {
      font-size: 1.15rem;
      text-align: inherit;
    }
  }

  /* Bottoni CTA: centrati verticalmente e orizzontalmente nel menu */
  .mega-menu__bottom-actions {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    @include flex-gap($space-0-5, column);
    width: auto;
    margin-bottom: 0;
  }

  .bottom-action,
  .bottom-action--banking,
  .bottom-action--fondazione {
    width: 220px !important;
    max-width: 220px !important;
    min-width: unset !important;
    height: 48px !important;
  }

  .bottom-action__text-primary { font-size: 0.85rem; }
  .bottom-action__text-secondary { font-size: 0.75rem; }
  .bottom-action__logo { max-width: 60px; padding-left: 1.2rem; }
}
</style>